/**
 * Kiosk Security — HSE/Admin audit of the site-gate kiosk's anti-spoofing layer.
 *
 * Surfaces every kiosk access attempt: desktop blocks, screen-photo spoofing,
 * liveness fails, suspected-screen flags, and wrong/ambiguous matches — plus a
 * one-click control to set the date after which liveness becomes MANDATORY.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ShieldAlert, MonitorX, ScanFace, AlertTriangle, ShieldCheck, Smartphone,
  RefreshCw, Lock,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPut } from '../lib/api';

const RESULT_BADGE = {
  device_blocked: { label: 'Desktop blocked', cls: 'bg-slate-200 text-slate-700' },
  spoofing_attempt: { label: 'Spoofing', cls: 'bg-rose-100 text-rose-700' },
  liveness_fail: { label: 'Liveness fail', cls: 'bg-amber-100 text-amber-800' },
  matched: { label: 'Matched', cls: 'bg-emerald-100 text-emerald-700' },
  no_match: { label: 'No match', cls: 'bg-slate-100 text-slate-600' },
  ambiguous_match: { label: 'Ambiguous', cls: 'bg-teal-100 text-teal-700' },
  no_enrolment: { label: 'No enrolment', cls: 'bg-slate-100 text-slate-500' },
  no_labour_on_project: { label: 'No labour', cls: 'bg-slate-100 text-slate-500' },
};

function StatCard({ icon: Icon, label, value, tone = 'slate', testid }) {
  const tones = {
    slate: 'text-slate-700 border-slate-200',
    rose: 'text-rose-600 border-rose-200 bg-rose-50/40',
    amber: 'text-amber-700 border-amber-200 bg-amber-50/40',
    indigo: 'text-teal-700 border-teal-200 bg-teal-50/40',
    emerald: 'text-emerald-700 border-emerald-200',
  };
  return (
    <div className={`bg-white border rounded-sm p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold opacity-80">
        <Icon size={13} /> {label}
      </div>
      <div className="text-3xl font-display font-bold mt-2 tabular-nums">{value ?? 0}</div>
    </div>
  );
}

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const num = (v) => (v === null || v === undefined ? '—' : v);

export default function KioskSecurity() {
  const [data, setData] = useState(null);
  const [result, setResult] = useState('');
  const [flagged, setFlagged] = useState(true);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const [cfg, setCfg] = useState(null);
  const [cfgDate, setCfgDate] = useState('');
  const [savingCfg, setSavingCfg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (result) params.set('result', result);
      else if (flagged) params.set('flagged', 'true');
      const r = await apiGet(`/labour-attendance/kiosk-security?${params.toString()}`);
      setData(r);
    } catch { toast.error('Failed to load kiosk security feed'); }
    finally { setLoading(false); }
  }, [days, result, flagged]);

  const loadCfg = useCallback(async () => {
    try {
      const c = await apiGet('/labour-attendance/liveness-config');
      setCfg(c);
      setCfgDate((c?.required_from || '').slice(0, 10));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  useEffect(() => { load(); }, [load]);

  const saveCfg = async (clear = false) => {
    setSavingCfg(true);
    try {
      const required_from = clear ? null : (cfgDate ? `${cfgDate}T00:00:00+00:00` : null);
      const r = await apiPut('/labour-attendance/liveness-config', { required_from });
      toast.success(clear ? 'Liveness enforcement cleared (grace period open)'
        : required_from ? 'Liveness enforcement date saved' : 'Cleared');
      await loadCfg();
      if (clear) setCfgDate('');
      setCfg((prev) => ({ ...(prev || {}), required_from: r.required_from, enforced_now: r.enforced_now }));
    } catch { toast.error('Failed to update liveness config'); }
    finally { setSavingCfg(false); }
  };

  const s = data?.summary || {};

  return (
    <div data-testid="kiosk-security-page">
      <PageHeader
        overline="Workforce · Security"
        title="Kiosk Security"
        subtitle="Anti-spoofing audit for the site-gate face kiosk — desktop blocks, screen-photo spoofing, liveness fails and wrong matches, across every site."
      />

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <StatCard icon={ScanFace} label="Total attempts" value={s.total} testid="kpi-total" />
        <StatCard icon={MonitorX} label="Desktop blocked" value={s.device_blocked} tone="slate" testid="kpi-device-blocked" />
        <StatCard icon={ShieldAlert} label="Spoofing" value={s.spoofing_attempts} tone="rose" testid="kpi-spoofing" />
        <StatCard icon={AlertTriangle} label="Liveness fails" value={s.liveness_fails} tone="amber" testid="kpi-liveness-fail" />
        <StatCard icon={Smartphone} label="Suspected screen" value={s.suspected_screen} tone="rose" testid="kpi-suspected-screen" />
        <StatCard icon={ShieldCheck} label="Low confidence" value={s.low_confidence} tone="indigo" testid="kpi-low-confidence" />
      </div>

      {/* Liveness enforcement control */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-5" data-testid="liveness-config-card">
        <div className="flex items-center gap-2 text-sm font-display font-bold text-slate-800 mb-1">
          <Lock size={15} className="text-teal-600" /> Mandatory liveness enforcement
        </div>
        <p className="text-xs text-slate-500 mb-3 max-w-3xl">
          Until this date, kiosk punches from older app builds (which don't send liveness signals) are allowed and logged as
          <span className="font-semibold"> unchecked</span>. On/after it, any punch without liveness signals is hard-rejected.
          Leave empty to keep the rollout grace period open.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Enforce from</label>
            <input
              type="date"
              value={cfgDate}
              onChange={(e) => setCfgDate(e.target.value)}
              className="border border-slate-200 rounded-sm text-sm px-3 py-2"
              data-testid="liveness-config-date"
            />
          </div>
          <button
            onClick={() => saveCfg(false)}
            disabled={savingCfg}
            className="bg-[#1F7A6E] text-white text-sm font-semibold px-4 py-2 rounded-sm hover:bg-[#14504E] disabled:opacity-50"
            data-testid="liveness-config-save"
          >Save</button>
          <button
            onClick={() => saveCfg(true)}
            disabled={savingCfg}
            className="text-slate-500 text-sm px-3 py-2 hover:text-slate-800"
            data-testid="liveness-config-clear"
          >Clear</button>
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cfg?.enforced_now ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
            data-testid="liveness-config-status"
          >
            {cfg?.enforced_now ? 'Enforced now' : cfg?.required_from ? 'Scheduled' : 'Grace period (not enforced)'}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="kiosk-security-filters">
        <label className="flex items-center gap-2 text-sm text-slate-700 font-semibold cursor-pointer pb-2" data-testid="filter-flagged">
          <input type="checkbox" checked={flagged} onChange={(e) => { setFlagged(e.target.checked); if (e.target.checked) setResult(''); }} />
          Flagged only
        </label>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Result</label>
          <select
            value={result}
            onChange={(e) => { setResult(e.target.value); if (e.target.value) setFlagged(false); }}
            className="border border-slate-200 rounded-sm text-sm px-3 py-2 bg-white min-w-[170px]"
            data-testid="filter-result"
          >
            <option value="">All results</option>
            <option value="device_blocked">Desktop blocked</option>
            <option value="spoofing_attempt">Spoofing attempt</option>
            <option value="liveness_fail">Liveness fail</option>
            <option value="matched">Matched</option>
            <option value="no_match">No match</option>
            <option value="ambiguous_match">Ambiguous match</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Window</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-200 rounded-sm text-sm px-3 py-2 bg-white" data-testid="filter-days">
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <button onClick={load} className="ml-auto flex items-center gap-2 text-sm text-slate-600 border border-slate-200 rounded-sm px-3 py-2 hover:bg-slate-50" data-testid="refresh-btn">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Events table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="kiosk-security-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-bold">Time</th>
              <th className="text-left px-3 py-2 font-bold">Result</th>
              <th className="text-left px-3 py-2 font-bold">Site</th>
              <th className="text-left px-3 py-2 font-bold">Worker</th>
              <th className="text-right px-3 py-2 font-bold">Motion</th>
              <th className="text-right px-3 py-2 font-bold">Texture</th>
              <th className="text-right px-3 py-2 font-bold">Moiré</th>
              <th className="text-right px-3 py-2 font-bold">Bright CV</th>
              <th className="text-left px-3 py-2 font-bold">Device</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.events || []).map((e) => {
              const badge = RESULT_BADGE[e.result] || { label: e.result || '—', cls: 'bg-slate-100 text-slate-600' };
              return (
                <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`event-row-${e.id}`}>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap text-xs">{fmtTs(e.ts)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                    {e.suspected_screen && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">screen?</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{e.project_name}</td>
                  <td className="px-3 py-2 text-slate-700">{e.matched_labour_name || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{num(e.motion_variance)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{num(e.texture_std_dev)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{num(e.moire_score)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{num(e.brightness_cv)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {e.access_blocked ? <span className="text-slate-700 font-semibold">{e.device_type_detected || 'desktop'}</span> : (e.client_ip || e.kiosk_id || '—')}
                  </td>
                </tr>
              );
            })}
            {!loading && (data?.events || []).length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400 text-sm" data-testid="empty-state">No matching events in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
